import { gql, TypedDocumentNode } from '@common/hooks';
import { ShiftType } from '@common/graphql';

export type ClinicalSpecialtyCustomType = {
  id: string;
  displayName: string;
  required?: boolean;
  __typename?: string;
};

export type CertificationCustomType = {
  id: string;
  name: string;
  credential?: string;
  required?: boolean;
  __typename?: string;
};

export type StaffingRequestType = {
  id: string;
  externalId?: string;
  startDate: string;
  staffingRequestType: 'long_term' | 'on_demand';
  description?: string;
  hoursPerShift: number;
  length: number;
  shiftsPerWeek: number;
  shiftType: ShiftType;
  billingRegularRate: number;
  estimatedCost: number;
  international: boolean;
  openStaffingRequestPositionsCount: number;
  filledStaffingRequestPositionsCount: number;
  activeApplicationsWithoutInactiveOffers: boolean;
  roleLevel: string;
  role: {
    id: string;
  };
  hiringLeader?: {
    id: string;
    name: string;
  };
  billingCard?: {
    id: string;
  };
  organizationUnit: {
    id: string;
    approvalChainTemplates?: {
      actionType: string;
      id: string;
    }[];
    department: {
      id: string;
      activeNonInternalWorksMemberships: {
        id: string;
        worksUser: {
          id: string;
          name: string;
        };
      }[];
    };
  };
  yearsOfExperience?: string;
  staffingRequestSpecialties?: {
    id: string;
    necessityLevel: string;
    clinicalSpecialty: {
      id: string;
      displayName: string;
    };
  }[];
  staffingRequestCertifications?: {
    id: string;
    necessityLevel: string;
    certification: {
      id: string;
      credential: string;
      name: string;
    };
  }[];
  staffingRequestTechnologies?: {
    id: string;
    technology: {
      id: string;
      name: string;
    };
  }[];
  canUpdateDetails: {
    value: boolean;
  };
  canUpdatePositionCount: {
    value: boolean;
  };
  canShowRateDetails: {
    value: boolean;
  };
  canUpdateRateAffectedDetails: {
    value: boolean;
  };
  staffingRequestTemplate: {
    id: string;
    billingRegularRate: number;
  };
};

export type ClinicalSpecialtyType = {
  id: string;
  displayName: string;
};

export type CertificationType = {
  id: string;
  name: string;
  credential: string;
};

export type TechnologyType = {
  name: string;
  id: string;
};

type StaffingRequestQueryData = {
  staffingRequest: StaffingRequestType;
  clinicalSpecialties: ClinicalSpecialtyType[];
  certificationList: CertificationType[];
  technologies: TechnologyType[];
};

type StaffingRequestQueryVariables = {
  id: string;
};

export const STAFFING_REQUEST_QUERY: TypedDocumentNode<
  StaffingRequestQueryData,
  StaffingRequestQueryVariables
> = gql`
  query StaffingRequestQuery($id: ID!) {
    staffingRequest(id: $id) {
      id
      externalId
      startDate
      staffingRequestType
      description
      hoursPerShift
      length
      shiftsPerWeek
      shiftType
      billingRegularRate
      estimatedCost
      international
      openStaffingRequestPositionsCount
      filledStaffingRequestPositionsCount
      activeApplicationsWithoutInactiveOffers
      roleLevel
      role {
        id
      }
      staffingRequestTemplate {
        id
        billingRegularRate
      }
      hiringLeader {
        id
        name
      }
      billingCard {
        id
      }
      organizationUnit {
        id
        approvalChainTemplates {
          actionType
          id
        }
        department {
          id
          activeNonInternalWorksMemberships {
            id
            worksUser {
              id
              name
            }
          }
        }
      }
      yearsOfExperience
      staffingRequestSpecialties {
        id
        necessityLevel
        clinicalSpecialty {
          id
          displayName
        }
      }
      staffingRequestCertifications {
        id
        necessityLevel
        certification {
          id
          credential
          name
        }
      }
      staffingRequestTechnologies {
        id
        technology {
          id
          name
        }
      }
      canUpdateDetails {
        value
      }
      canUpdatePositionCount {
        value
      }
      canShowRateDetails {
        value
      }
      canUpdateRateAffectedDetails {
        value
      }
    }
    clinicalSpecialties {
      id
      displayName
    }
    certificationList {
      id
      name
      credential
    }
    technologies {
      name
      id
    }
  }
`;
